//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { EmailTemplate } from '@apis/account-settings/emailSettingsApiModel';
import { ContentSourceProductsSortBy } from '@apis/campaigns/contentSourceApiModel';
import { EngagementPostType } from '@apis/instagram-user/instagramUserApiModel';
import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { ProfileNetwork } from '@influencers/constants/influencersConstants';
import { EngagementPostView } from '@instagram-account/redux/engagement/posts/instagramAccountEngagementPostsModel';
import CollectionId from '@models/CollectionId';
import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import DraftId from '@models/DraftId';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import PinterestPinId from '@models/PinterestPinId';
import PostableId from '@models/PostableId';
import { SamplingCampaignGroupId } from '@models/SamplingCampaignGroupId';
import { SamplingCampaignId } from '@models/SamplingCampaignId';
import { SamplingCampaignRequestId } from '@models/SamplingCampaignRequestId';
import { SocialNetwork } from '@models/SocialNetwork';
import { TikTokTrendingHashtagId } from '@models/TikTokTrendingHashtagId';
import { PinterestScheduleType } from '@pinterest/redux/post-details/pinterestPostDetailsModel';
import { CustomReportId } from '@reporting/custom-reporting/types/CustomReportId';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { isDefined, isNotNull } from '@utils/filterPredicates';

import { RoutableStateName } from 'app/top-level-states';

/**
 * This is the master interface for all typed navigation routes in the dashboard.
 * Future state param interfaces should be added here, indexed by the full state
 * name that they pertain to.
 */
export interface IStateParams {
  'account-settings.social': {
    ['oauth-network']?: string;
    ['oauth-error']?: string;
  };

  'agentsDashboard.chat': {
    threadId?: string;
    initialMessage?: string;
    messageId?: string;
  };

  'campaigns.content-source-available-reviews': {
    currentPage?: number;
    itemsPerPage?: number;
    productId?: string;
    searchText?: string;
    sortBy?: string;
    viewCart?: boolean;
  };

  'campaigns.content-source-cart-checkout': {
    currentPage?: number;
    itemsPerPage?: number;
    productId?: string;
    searchText?: string;
    sortBy?: ContentSourceProductsSortBy;
  };

  'campaigns.content-source-orders': {
    currentPage?: number;
    itemsPerPage?: number;
    searchText?: string;
    sortBy?: string;
  };

  'custom-reporting': {
    reportId: CustomReportId;
    dateRange?: DateString;
  };

  'influencers.list': {
    listId: number;
  };

  'instagramAccount.feedEngagement': {
    endDate?: string;
    engagementPostFilter?: EngagementPostType;
    graphMetric?: string;
    postViewType?: EngagementPostView;
    startDate?: string;
  };

  'instagramAccount.like2buy-preview': {
    storeId: Like2BuyV2StoreId;
  };

  'instagramAccount.storyEngagement.index': {
    endDate?: string;
    graphMetric?: string;
    startDate?: string;
  };

  'listening.topics': {
    topicId?: string;
  };

  'library.collections.content': {
    collectionId?: CollectionId;
  };

  'youtube-oauth': {
    code?: string;
    error?: string;
    state?: string;
  };

  'pinterest.schedule.postDetails': {
    copiedPinId?: PinterestPinId;
    copiedScheduledPostId?: PostableId;
    copiedDraftId?: DraftId;
    draftPostId?: DraftId;
    postTimeMs?: number;
    scheduleType?: PinterestScheduleType;
    scheduledPostId?: PostableId;
    targetCuids?: Cuid[];
  };

  'pinterest.schedule.select': {
    postTimeMs?: number;
  };

  'schedule.calendar': {
    startDate?: DateString;
    endDate?: DateString;
    view?: UniversalCalendarView;
    platform?: SocialNetwork;
  };

  'schedule.drafts': {
    platform?: SocialNetwork;
  };

  'schedule.inspiration': {
    dateRange?: string;
    category?: string;
  };

  'schedule.inspiration-hashtag': {
    category?: string;
    dateRange?: string;
    hashtagId: TikTokTrendingHashtagId;
    network: SocialNetwork;
  };

  'schedule.scheduler': {
    copiedDraftId?: DraftId;
    copiedScheduledPostId?: PostableId;
    draftPostId?: DraftId;
    postTimeMs?: number;
    postType?: UniversalPostType[];
    scheduledPostId?: PostableId;
    targetCuid?: Cuid;
    hashtag?: string[];
  };

  // URL query-param values are always strings. useSocialInboxUrlParams()
  // translates them into the typed SocialInboxUrlParams interface
  // (e.g. string → boolean for filter flags like isRead, isResponded, etc.).
  socialresponse: {
    comment?: string;
    endTime?: string;
    facebook?: string;
    instagram?: string;
    intents?: string;
    isRead?: string;
    isResponded?: string;
    isUrgent?: string;
    mention?: string;
    startTime?: string;
    tiktok?: string;
  };

  'account-settings.creator-email-settings-edit': {
    emailTemplateId?: EmailTemplate['id'];
  };

  'campaigns.creators-manage-orders-details': {
    campaignSlugName: string;
    creatorUsername: string;
    orderId: string;
    network?: ProfileNetwork;
  };

  'campaigns.creators-details': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-configuration': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-manage-creators': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-manage-proposals': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-manage-messages': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-manage-affiliates': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-manage-orders': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-manage-payments': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-content-approval': {
    campaignSlugName: string;
    view?: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-contents-social-contents-media': {
    campaignSlugName: string;
    view?: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.creators-contents-social-contents-sentiments': {
    campaignSlugName: string;
    fromReportId?: CustomReportId;
  };

  'walmart-oauth': {
    code?: string;
    type?: string;
    clientId?: string;
    state?: string;
    partnerId?: string;
  };

  youtube: {
    startDate?: DateString;
    endDate?: DateString;
  };

  'campaigns.creators-manage-payments-history': {
    campaignSlugName?: string;
    creatorUsername?: string;
    network?: ProfileNetwork;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-request-details': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-quote': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-edit': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-edit-products': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-edit-audience': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-edit-tasks': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-edit-packaging': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-edit-fulfillment': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-edit-purchase-options': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign-request-details-edit-summary': {
    campaignId: SamplingCampaignRequestId;
  };

  'campaigns.sampling-campaign': {
    campaignId: SamplingCampaignId;
  };

  'campaigns.sampling-campaign-group': {
    groupId: SamplingCampaignGroupId;
  };

  'campaigns.sampling-campaign-group-details-overview': {
    groupId: SamplingCampaignGroupId;
  };

  'campaigns.sampling-campaign-details-overview': {
    campaignId: SamplingCampaignId;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-request': {
    campaignId: SamplingCampaignId;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-social': {
    campaignId: SamplingCampaignId;
    view?: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-social-media': {
    campaignId: SamplingCampaignId;
    view?: string;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-social-syndication': {
    campaignId: SamplingCampaignId;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-review-products': {
    campaignId: SamplingCampaignId;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-review-reviews': {
    campaignId: SamplingCampaignId;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-review-syndication': {
    campaignId: SamplingCampaignId;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-review-media': {
    campaignId: SamplingCampaignId;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-details-post-survey': {
    campaignId: SamplingCampaignId;
    fromReportId?: CustomReportId;
  };

  'campaigns.sampling-campaign-group-details-social': {
    groupId: SamplingCampaignGroupId;
    view?: string;
  };

  'campaigns.sampling-campaign-group-details-social-media': {
    groupId: SamplingCampaignGroupId;
    view?: string;
  };

  'campaigns.sampling-campaign-group-details-post-survey': {
    groupId: SamplingCampaignGroupId;
  };

  'campaigns.sampling-campaign-group-details-review': {
    groupId: SamplingCampaignGroupId;
  };

  'campaigns.sampling-campaign-group-details-review-products': {
    groupId: SamplingCampaignGroupId;
  };

  'campaigns.sampling-campaign-group-details-review-reviews': {
    groupId: SamplingCampaignGroupId;
  };

  'campaigns.sampling-campaign-group-details-review-syndication': {
    groupId: SamplingCampaignGroupId;
  };

  'campaigns.sampling-campaign-group-details-review-media': {
    groupId: SamplingCampaignGroupId;
    view?: string;
  };
}

export type TypedRoutableStateName = keyof IStateParams;

export type StateParams<T extends RoutableStateName> =
  T extends TypedRoutableStateName ? IStateParams[T] : never;

/**
 * Converts a given optional value to a string if it is not already a string or string-based.
 *
 * Ex --> StringifyValue<number> = string
 * Ex --> StringifyValue<InstagramPostType | undefined> = InstagramPostType | undefined
 */
type StringifyValue<T> = T extends string | undefined
  ? T | undefined
  : T extends string
    ? T
    : string;

/**
 * This helper type converts an interface with any value types to one that contains
 * only string-based types, while maintaining the optional status.
 *
 * Ex --> Stringify<{ val1: number, val2?: SomeEnum }> = { val1: string, val2?: SomeEnum }
 */
type Stringify<T extends Record<string, unknown>> = {
  [idx in keyof T]: StringifyValue<T[idx]>;
};

/**
 * State params are serialized into strings as they are passed into the URL,
 * so any number or number-based tinytype values will ultimately be outputted
 * as strings when we read them out of the state.
 *
 * The consumer of these types is then responsible for deserializing.
 */
export type StringifiedStateParams<T extends TypedRoutableStateName> =
  Stringify<StateParams<T>>;

/**
 * A helper to convert a string | undefined serialized query param
 * into a number, number-based tinytype, or undefined deserialized value.
 *
 * @param param The serialized param string
 */
export const deserializeOptionalNumericalParam = <T extends number>(
  param?: string | null,
): T | undefined => {
  return isDefined(param) && isNotNull(param)
    ? (parseInt(param, 10) as T)
    : undefined;
};

/**
 * A helper to convert a string | undefined serialized query param
 * into a boolean or undefined deserialized value.
 *
 * @param param The serialized param string
 */
export const deserializeOptionalBooleanParam = (
  param?: string | null,
): boolean | undefined => {
  if (isDefined(param) && isNotNull(param)) {
    switch (param.toLowerCase()) {
      case 'true':
        return true;
      case 'false':
        return false;
    }
  }

  return undefined;
};

/**
 * A helper to convert a string | undefined serialized query param
 * into a enum or undefined deserialized value.
 *
 * @param param The serialized param string
 */
export const deserializeOptionalEnumParam = <K extends string, E>(
  enumDef: Record<K, E>,
  param?: string | null,
): E | undefined => {
  return isDefined(param) &&
    isNotNull(param) &&
    Object.values(enumDef).includes(param)
    ? (param as E)
    : undefined;
};
